import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import * as Apollo from '@apollo/client'
import { useQuery } from '@apollo/client'
import { OneDataCollection, SecondaryActionItem } from '@factorialco/f0-react/dist/experimental'
import { Download, Pencil, Settings, Share, Upload } from '@factorialco/f0-react/icons/app'
import { ResultOf } from '@graphql-typed-document-node/core'
import {
  CompensationsListColumnsDocument,
  CompensationsListColumnsQuery,
  CompensationsListColumnsQueryVariables,
  CompensationsListFragment,
  CompensationsValueObjectsEmployeeCompensationValidationTypeEnum,
  EmployeePayrollRunElementInputEmployeePayrollRunCompensationFragment,
  EmployeePayrollRunInputRowPayrollRunCompensationFragment,
  EmployeePayrollRunTotalsFragment,
  EmployeePayrollRunsDocument,
  IntegrationsAgreggatedSyncableStateValuesEnum,
  PayrollResultFragment,
  ValidationTypeFiltersDocument,
} from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  EmployeePayrollRunNode,
  employeePayrollRunElement,
  formatCellValue,
} from 'modules/compensations/components/EmployeePayrollRunElements'
import { useCompensationsListFilterOptions } from 'modules/compensations/hooks/useCompensationsListFilterOptions'
import { useCompensationsRefresh } from 'modules/compensations/hooks/useCompensationsRefresh'
import { useSyncCompletion } from 'modules/compensations/hooks/useSyncCompletion'
import { useCurrencyContext } from 'modules/compensations/lib/CurrencyContext'
import { usePayrollRuns } from 'modules/compensations/lib/PayrollRunsContext'
import {
  calculateTotalAlerts,
  createCountItemsWithAlerts,
  processCompensationsFilters,
  renderAlertsColumn,
  renderEmployeeColumn,
} from 'modules/compensations/lib/compensationsListHelpers'
import { EUR } from 'modules/compensations/lib/currency'
import { employeeStatusFilterConfig } from 'modules/compensations/types/filters'
import { SyncStatusColumnItem } from 'modules/integrations/public'
import {
  type UseSyncActionProps,
  useSyncAction as useDataCollectionSyncAction,
  useIntegrationsSyncContext,
  useSyncColumn,
  useSyncStatusFilter,
} from 'modules/integrations/public'
// TODO: We should avoid coupling modules because it hurts application performance - in order to execute this
//  compensations page, browser will have to upload the payroll_bundle as well, so, a good approach would be to
//  move this logic to the core or potentially to the compensations module. When deprecating the old payroll product we'll
//  have to migrate payroll concepts inside Compensation and this logic as well, but until then the same concept will
//  work for different products
import {
  getUnits,
  isUnitKey,
} from 'modules/payroll_bundle/hooks/usePayrollConceptCompensationUnits'

import { ImportModal, ImportType } from './ImportModal'
import { getHeaderOptions } from './utils'

/**
 * __useCompensationsListColumnsQuery__
 *
 * To run a query within a React component, call `useCompensationsListColumnsQuery` and pass it any options that fit your needs.
 * When your component renders, `useCompensationsListColumnsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useCompensationsListColumnsQuery({
 *   variables: {
 *      ids: // array of 'payrollRunIds'
 *   },
 * });
 */
function useCompensationsListColumnsQuery(
  baseOptions: Apollo.QueryHookOptions<
    CompensationsListColumnsQuery,
    CompensationsListColumnsQueryVariables
  >
) {
  const options = { ...baseOptions }
  return Apollo.useQuery<CompensationsListColumnsQuery, CompensationsListColumnsQueryVariables>(
    CompensationsListColumnsDocument,
    options
  )
}

type CycleCompensationsListOnConfirmSync = {
  onConfirmSync: UseSyncActionProps['onConfirmSync']
}

type CycleCompensationsListProps = {
  payrollRun: CompensationsListFragment
  onEdit: (employeePayrollRunInputRowEmployeePayrollRun: EmployeePayrollRunNode) => void
  locale: string
  onOpenExportSidePanel: () => void
  onManagePayrollResultsClick: () => void
  payrollResults: PayrollResultFragment[]
  showImportResults: boolean
  refreshFnRef: React.MutableRefObject<(() => void) | null>
} & CycleCompensationsListOnConfirmSync &
  (
    | {
        showSendToBookkeeper: true
        onSendToBookkeeperClick: () => void
      }
    | {
        showSendToBookkeeper?: false
      }
  )

type CompensationsListFilters = {
  companyId: number
  featureFlagNames: string[]
  payrollRunId: number
  legalEntityIds?: number[]
  teamIds?: number[]
  workplaceIds?: number[]
  validationTypes?: CompensationsValueObjectsEmployeeCompensationValidationTypeEnum[]
  integrationsAgreggatedSyncableStates?: IntegrationsAgreggatedSyncableStateValuesEnum[]
}

function CycleCompensationsList({
  payrollRun,
  onEdit,
  locale,
  onOpenExportSidePanel,
  onManagePayrollResultsClick,
  payrollResults,
  showImportResults,
  showSendToBookkeeper,
  refreshFnRef,
  onConfirmSync,
  ...rest
}: CycleCompensationsListProps & { onSendToBookkeeperClick?: () => void }) {
  const onSendToBookkeeperClick = rest.onSendToBookkeeperClick
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [currency] = useCurrencyContext()

  const [filters, setCompensationsFilters] = useState<CompensationsListFilters | undefined>()
  const [importModalOpen, toggleImportModalOpen] = useToggle(false)

  // refreshCompensations is used to trigger data refresh after sync completion
  // to ensure the compensations list shows the latest data from the integration
  // Remove this once the DataOneCollection is updated to support cache invalidation dynamically
  const { refreshKey, refreshCompensations } = useCompensationsRefresh({ refreshFnRef })

  const defaultFilters = useMemo(
    () => ({
      companyId,
      featureFlagNames: [],
      payrollRunId: Number(payrollRun.id),
    }),
    [companyId, payrollRun.id]
  )

  const { data } = useCompensationsListColumnsQuery({
    variables: filters ?? defaultFilters,
    skip: false,
  })
  const { marketplaceIntegration, setDataCollectionSource, lastSyncRun, isSyncRunning } =
    useIntegrationsSyncContext()

  const getStatusesFromSyncable = useCallback((syncable: SyncStatusColumnItem) => {
    if (syncable.__typename !== 'CompensationsPayrollRunEmployee')
      throw new Error('Not an employee payroll run')

    return syncable.compensations
      .filter((compensation) => compensation.resultType === 'compensation')
      .flatMap((compensation) => compensation.syncableState?.map((s) => s.status)[0] ?? null)
  }, [])

  const syncColumn = useSyncColumn({ getStatusesFromSyncable: getStatusesFromSyncable })
  const syncStatusFilter = useSyncStatusFilter()

  const { dataCollectionSyncAction, syncModal } = useDataCollectionSyncAction({
    onConfirmSync,
    confirmTrackingEvent: trackingEvents.compensations.syncToPayrollSoftware,
  })

  const createExportAction = useCallback((): SecondaryActionItem => {
    return {
      label: i18n.t('compensations.PayrollRunCompensationsList.export'),
      icon: Upload,
      onClick: onOpenExportSidePanel,
    }
  }, [onOpenExportSidePanel])

  const secondaryActions = useMemo<SecondaryActionItem[]>(() => {
    const hasSyncActions = dataCollectionSyncAction.length > 0

    // 1. Sync actions (if any)
    const actions: SecondaryActionItem[] = [...dataCollectionSyncAction]

    // 2. Export (only if no sync actions, otherwise it goes after import)
    if (!hasSyncActions) {
      actions.push(createExportAction())
    }

    // 3. Send to bookkeeper (conditional)
    if (showSendToBookkeeper) {
      actions.push({
        label: i18n.t('compensations.PayrollRunCompensationsList.send_to_bookkeeper'),
        icon: Share,
        onClick: onSendToBookkeeperClick,
      })
    }

    // 4. Import (always shown)
    actions.push({
      label: i18n.t('compensations.PayrollRunCompensationsList.import'),
      icon: Download,
      onClick: () => toggleImportModalOpen(),
    })

    // 5. Export (only if sync actions exist, goes after import)
    if (hasSyncActions) {
      actions.push(createExportAction())
    }

    // 6. Manage payroll results (conditional)
    if (showImportResults && payrollResults.length > 0) {
      actions.push({
        label: i18n.t('compensations.PayrollRunCompensationsList.manage_payroll_results.button'),
        icon: Settings,
        onClick: onManagePayrollResultsClick,
      })
    }

    return actions
  }, [
    dataCollectionSyncAction,
    createExportAction,
    showSendToBookkeeper,
    onSendToBookkeeperClick,
    showImportResults,
    payrollResults.length,
    onManagePayrollResultsClick,
    toggleImportModalOpen,
  ])

  // This is a workaround to refresh the compensations data when the sync is complete
  useSyncCompletion({
    lastSyncRun,
    isSyncRunning,
    onSyncComplete: refreshCompensations,
  })

  const { currentPayrollRun, setCurrentPayrollRun } = usePayrollRuns()

  const { data: validationFiltersData } = useQuery(ValidationTypeFiltersDocument, {
    variables: {
      payrollRunId: payrollRun.id,
    },
  })

  const validationFiltersDataResult = validationFiltersData?.compensations

  const { loadLegalEntityOptions, loadTeamOptions, loadWorkplaceOptions } =
    useCompensationsListFilterOptions(companyId)

  const countItemsWithAlerts = useMemo(
    () => createCountItemsWithAlerts(validationFiltersDataResult),
    [validationFiltersDataResult]
  )

  const totalAlerts = useMemo(
    () => calculateTotalAlerts(validationFiltersDataResult),
    [validationFiltersDataResult]
  )

  const renderEmployeeColumnBound = useCallback(
    (item: EmployeePayrollRunNode) => renderEmployeeColumn(item),
    []
  )

  const elements = useMemo(
    () => data?.compensations.elementPayrollConceptsConnection.nodes ?? [],
    [data?.compensations.elementPayrollConceptsConnection.nodes]
  )

  const hasPayrollResultConcepts = useMemo(
    () =>
      elements.some(
        (element: EmployeePayrollRunInputRowPayrollRunCompensationFragment) =>
          element.resultType === 'payroll_result'
      ),
    [elements]
  )

  const conceptColumns = useMemo(() => {
    const unitsConfiguration = getUnits({ money: currency.isoCode })

    return elements.map((element: EmployeePayrollRunInputRowPayrollRunCompensationFragment) => {
      const options = hasPayrollResultConcepts ? getHeaderOptions(element.resultType) : null
      const elementPayrollConceptId = element.payrollConceptId
      const elementResultType = element.resultType

      return {
        label: element.label,
        summary: element.id,
        infoIcon: options?.infoIcon,
        info: options?.info,
        align: 'right' as const,
        render: (item: EmployeePayrollRunNode) => {
          const employeePayrollRunCompensation = item?.compensations?.find(
            (comp: EmployeePayrollRunElementInputEmployeePayrollRunCompensationFragment) =>
              comp.payrollConcept?.id === elementPayrollConceptId &&
              comp.resultType === elementResultType
          )

          return employeePayrollRunElement({
            employeePayrollElement: employeePayrollRunCompensation,
            locale,
            unitsConfiguration,
          })
        },
      }
    })
  }, [locale, elements, hasPayrollResultConcepts, currency.isoCode])

  const createPayrollTotalsMap = useCallback(
    (
      payrollRunTotals: EmployeePayrollRunTotalsFragment[],
      elementsParam: EmployeePayrollRunInputRowPayrollRunCompensationFragment[],
      currencyIsoCode?: string
    ): Record<string, string> => {
      const result: Record<string, string> = {}

      elementsParam.forEach((element) => {
        result[element.id] = '-'
      })

      const elementsMap = elementsParam.reduce<
        Record<string, EmployeePayrollRunInputRowPayrollRunCompensationFragment>
      >((map, element) => {
        map[element.id] = element
        return map
      }, {})

      const safeCurrencyIsoCode = currencyIsoCode || EUR.isoCode
      const unitsConfig = getUnits({ money: safeCurrencyIsoCode })

      payrollRunTotals.forEach((total) => {
        if (total.totalAmountInCents == null) {
          return
        }

        const element = elementsMap[total.id]
        const key = element?.id ?? total.id

        if (element?.name) {
          const unit = element.payrollConceptUnit
          if (unit && isUnitKey(unit)) {
            const unitSymbol = unitsConfig[unit]
            result[key] = formatCellValue(
              unit,
              unitSymbol,
              Number(total.totalAmountInCents),
              locale
            )
            return
          }
        }

        result[key] = formatCellValue(
          'money',
          safeCurrencyIsoCode,
          Number(total.totalAmountInCents),
          locale
        )
      })

      return result
    },
    [locale]
  )

  const summaries = useMemo(() => {
    const result: { [key: string]: { type: 'sum' } } = {}

    elements.forEach((element: EmployeePayrollRunInputRowPayrollRunCompensationFragment) => {
      result[element.id] = {
        type: 'sum',
      }
    })

    return result
  }, [elements])

  const mapSummariesFn = useCallback(
    (queryData: ResultOf<typeof EmployeePayrollRunsDocument>) => {
      return queryData.compensations.payrollRunEmployeesConnection.nodes[0]
        ? {
            ...queryData.compensations.payrollRunEmployeesConnection.nodes[0],
            ...createPayrollTotalsMap(
              queryData.compensations.payrollRunTotalsConnection.nodes ?? [],
              elements,
              currency.isoCode
            ),
          }
        : undefined
    },
    [createPayrollTotalsMap, elements, currency.isoCode]
  )

  const validSyncStateValues = useMemo(() => {
    if (!syncStatusFilter.integrationsAgreggatedSyncableStates?.options?.options) {
      return []
    }

    const options = syncStatusFilter.integrationsAgreggatedSyncableStates.options.options

    if (!Array.isArray(options)) {
      return []
    }

    return options
      .map((option) => option?.value)
      .filter(
        (value): value is IntegrationsAgreggatedSyncableStateValuesEnum => typeof value === 'string'
      )
  }, [syncStatusFilter])

  const source = useGraphqlDataCollectionSource(
    {
      document: EmployeePayrollRunsDocument,
      fetchPolicy: 'cache-and-network',
      mapData: (queryData) => queryData.compensations.payrollRunEmployeesConnection.nodes,
      paginationType: 'infinite-scroll',
      summaries,
      mapSummaries: mapSummariesFn,
      mapPagination: (queryData) => {
        return {
          total: queryData.compensations.payrollRunEmployeesConnection.totalCount ?? 0,
          cursor: queryData.compensations.payrollRunEmployeesConnection.pageInfo.endCursor ?? '0',
          hasNextPage:
            queryData.compensations.payrollRunEmployeesConnection.pageInfo.hasNextPage ?? false,
        }
      },
      mapVariables: ({ sortings, search, filters: collectionFilters }) => {
        const {
          legalEntityIds,
          teamIds,
          workplaceIds,
          validationTypes,
          employeeStatus,
          integrationsAgreggatedSyncableStates,
        } = processCompensationsFilters(collectionFilters, validSyncStateValues)

        // TODO: this is a hack to set the filters in the parent component until
        // the DataOneCollection is updated to support columns dynamically
        const currentFilters = {
          companyId,
          featureFlagNames: [],
          payrollRunId: payrollRun.id,
          legalEntityIds,
          teamIds,
          workplaceIds,
          validationTypes,
          integrationsAgreggatedSyncableStates,
        }

        setCompensationsFilters(currentFilters)

        // TODO: we need to check if still makes sense having the payroll run context
        if (payrollRun.id) {
          setCurrentPayrollRun(Number(payrollRun.id))
        }

        const sortOrder = sortings?.[0]
          ? {
              field: sortings[0].field,
              order:
                sortings[0].field === 'alerts' && !sortings[0].order
                  ? ('asc' as const)
                  : sortings[0].order,
            }
          : { field: 'fullName' as const, order: 'asc' as const }

        return {
          payrollRunId: payrollRun.id,
          search: search,
          sortOrder,
          legalEntityIds,
          teamIds,
          workplaceIds,
          marketplaceIntegrationId: marketplaceIntegration?.id || 0,
          includeSyncableState: !!marketplaceIntegration,
          validationTypes,
          integrationsAgreggatedSyncableStates,
          employeeStatus,
          companyId,
        }
      },
      search: {
        enabled: true,
      },
      defaultSortings: {
        field: 'fullName',
        order: 'asc',
      },
      filters: {
        legalEntityIds: {
          label: i18n.t('compensations.PayrollRunCompensationsList.filters.legalEntityColumnLabel'),
          type: 'in',
          options: {
            cache: false,
            options: () => loadLegalEntityOptions(payrollRun),
          },
        },
        teamIds: {
          label: i18n.t('compensations.PayrollRunCompensationsList.filters.teamColumnLabel'),
          type: 'in',
          options: {
            cache: true,
            options: loadTeamOptions,
          },
        },
        workplaceIds: {
          label: i18n.t('compensations.PayrollRunCompensationsList.filters.workplaceColumnLabel'),
          type: 'in',
          options: {
            cache: true,
            options: loadWorkplaceOptions,
          },
        },
        ...syncStatusFilter,
        ...{
          validationTypes: {
            label: i18n.t(
              'compensations.PayrollRunCompensationsList.filters.validationTypeColumnLabel'
            ),
            type: 'in',
            options: {
              options: [
                {
                  label: i18n.t(
                    'compensations.PayrollRunCompensationsList.filters.pendingAlertColumnLabel'
                  ),
                  value: 'pending_to_define',
                },
                {
                  label: i18n.t(
                    'compensations.PayrollRunCompensationsList.filters.warningAlertColumnLabel'
                  ),
                  value: 'above_limit',
                },
                {
                  label: i18n.t(
                    'compensations.PayrollRunCompensationsList.filters.discrepancyAlertColumnLabel'
                  ),
                  value: 'discrepancy',
                },
              ].filter((option) => option && option.value != null),
            },
          },
        },
        ...employeeStatusFilterConfig(),
      },
      presets: [
        ...(countItemsWithAlerts('above_limit')() > 0
          ? [
              {
                label: i18n.t(
                  'compensations.PayrollRunCompensationsList.filters.warningAlertColumnLabel'
                ),
                filter: { validationTypes: ['above_limit'] },
                itemsCount: countItemsWithAlerts('above_limit'),
              },
            ]
          : []),
        ...(countItemsWithAlerts('pending_to_define')() > 0
          ? [
              {
                label: i18n.t(
                  'compensations.PayrollRunCompensationsList.filters.pendingAlertColumnLabel'
                ),
                filter: { validationTypes: ['pending_to_define'] },
                itemsCount: countItemsWithAlerts('pending_to_define'),
              },
            ]
          : []),
        ...(countItemsWithAlerts('discrepancy')() > 0
          ? [
              {
                label: i18n.t(
                  'compensations.PayrollRunCompensationsList.filters.discrepancyAlertColumnLabel'
                ),
                filter: { validationTypes: ['discrepancy'] },
                itemsCount: countItemsWithAlerts('discrepancy'),
              },
            ]
          : []),
      ],
      itemOnClick: (item: EmployeePayrollRunNode) => () => onEdit(item),
      itemActions: (item: EmployeePayrollRunNode) => [
        {
          label: i18n.t('compensations.PayrollRunCompensationsList.updateActionLabel'),
          icon: Pencil,
          onClick: () => {
            item.id && onEdit(item)
          },
        },
        {
          label: i18n.t('compensations.PayrollRunCompensationsList.goToContractActionLabel'),
          icon: Share,
          onClick: () => {
            item.employee?.id &&
              window.open(
                navigation.employees.id.contractVersions.fullPath({
                  id: String(item.employee.id),
                }),
                '_blank'
              )
          },
        },
      ],
      secondaryActions: {
        expanded: 2,
        actions: () => secondaryActions,
      },
    },
    [
      setCurrentPayrollRun,
      navigation,
      navigate,
      onEdit,
      locale,
      refreshKey,
      countItemsWithAlerts,
      dataCollectionSyncAction,
      summaries,
    ]
  )

  setDataCollectionSource(source)

  const handleImportSubmit = (importType: ImportType) => {
    const importUrl =
      importType === 'importResult'
        ? navigation.compensations.cycles.id.importResult
        : importType === 'compensations'
        ? navigation.compensations.cycles.id.importCompensations
        : undefined

    if (importUrl) {
      navigate(
        importUrl
          .fullPath({ id: currentPayrollRun?.id || '' })
          .replace(/\*+$/, '')
          .concat(`?payroll-run-id=${currentPayrollRun?.id}`)
      )
    }

    toggleImportModalOpen()

    track(trackingEvents.compensations.startImportCompensations, {
      payroll_run_id: currentPayrollRun?.id,
      import_type: importType,
    })
  }

  const handleStateChange = (state: { filters?: unknown }) => {
    const newFilters = JSON.stringify(state.filters)

    const hasFilterChanges = !filters || JSON.stringify(filters) !== newFilters

    if (hasFilterChanges) {
      track(trackingEvents.compensations.applyFilters, {
        new_filters: newFilters,
      })
    }
  }

  const alertsColumn = useMemo(
    () =>
      totalAlerts > 0
        ? {
            label: i18n.t('compensations.PayrollRunCompensationsList.alertsColumnLabel'),
            info: i18n.t('compensations.PayrollRunCompensationsList.alertsColumnTooltip'),
            sorting: 'alerts' as const,
            render: renderAlertsColumn,
          }
        : null,
    [totalAlerts]
  )

  return (
    <>
      <OneDataCollection
        id="compensations/v1"
        fullHeight
        emptyStates={{
          'no-data': {
            title: i18n.t('compensations.employeePayrollRunElementCell.empty'),
          },
          'no-results': {
            title: i18n.t('compensations.employeePayrollRunElementCell.empty_results.title'),
            description: i18n.t(
              'compensations.employeePayrollRunElementCell.empty_results.description'
            ),
            emoji: '🧐',
          },
        }}
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('compensations.PayrollRunCompensationsList.employeeColumnLabel'),
                  sorting: 'fullName',
                  render: renderEmployeeColumnBound,
                },
                ...(conceptColumns.length > 0
                  ? syncColumn.map((col) => ({ ...col, align: 'right' as const }))
                  : []),
                ...(alertsColumn ? [alertsColumn] : []),
                ...conceptColumns,
              ],
              frozenColumns: 1,
            },
          },
        ]}
        onStateChange={handleStateChange}
      />
      {syncModal}
      {importModalOpen && (
        <ImportModal onClose={() => toggleImportModalOpen()} onSubmit={handleImportSubmit} />
      )}
    </>
  )
}

export { CycleCompensationsList }
export type { CycleCompensationsListOnConfirmSync }
